import React, { useState, useEffect } from "react";
import MapContainer from "./components/MapContainer3";
import LayerControls from "./components/LayerControls";
import TimeSeriesChart from "./components/TimeSeriesChart";
import {getRiskData} from "./components/get_fdra_risk";
import { getNFDRS, getNFDRSbyStn, getNfdrsClimo } from "./nfdrs_to_geojson";
import { ForecastSelector } from "./components/ForecastSelector";
import moment from "moment-timezone";
import "bootstrap/dist/css/bootstrap.min.css";
import './App.css';
import { nfdrs_risk_colors, risk_ids } from "./components/nfdrs_colors";
import { Colorbar } from "./components/colorbar";
import colorMapping from "./components/colorMapping";
import SawtiCalendar from "./components/sawti_calander";
import {wims_meta} from './wims_ids';

const App = () => {

  const host = window.location.host === "xchge.nysmesonet.org" || window.location.host.split(":")[0] === "localhost";
  const domain = host ? 'https://xchge.nysmesonet.org': 'https://chge.nysmesonet.org'; 


  const [visibleLayers, setVisibleLayers] = useState({
    chge: true,
    nys: true,
    counties: true,
    us_states: true,
    fdras: false,
    incidents: true,
    alerts: true,
    lightning: true,
    radar: false,
  });

  const now = moment.utc().subtract(1, "H");
  const chart_start_date =  moment.utc().subtract(1, "weeks");
  const [selectedDateTime, setSelectedDateTime] = useState(now);
  const [stationData, setStationData] = useState([]);
  const [nfdrs_modified_dt, setNfdrsMtime] = useState(null);
  const [forecastData, setForecastData] = useState(null);

  const [selectedVariable, setSelectedVariable] = useState("sawti");
  const [selectedStation, setSelectedStation] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [timeSeriesNormals, setTimeSeriesNormals] = useState([]);
  const [isNFDRS, setIsNFDRS] = useState(false);
  const [display, setDisplay] = useState("obs");
  const [risk, setRisk] = useState(null);
  const [hudsonRisk , setHvRisk] = useState(null);
  const [catskillRisk , setCtRisk] = useState(null);
  const [lastModified, setLastModified] = useState(null); // SAWTI/NYSM last modified time
  const [colorInfo, setColorInfo] = useState({});
//   const [forecastDisplayMode, setForecastDisplayMode] = useState("clipped");


  // forecast model controls
  const [forecastSelection, setForecastSelection] = useState({
    model:"HRRR",
    day: 1,
  });



    const loadRisk = async () => {
        const color_metadata = {};
        const result = await getRiskData();
        setRisk(result);
        setHvRisk(result?.find(r => r.name === "Hudson Valley"));
        setCtRisk(result?.find(r => r.name === "Catskill"));
        result.forEach((j) => {
            color_metadata[j['name']] = j['risk'];
        });
        setColorInfo(color_metadata);
    };
    useEffect (() => {
        loadRisk();

    },[]);
        // console.log("RISK", risk[0]['effective_date'])



  const handleLayerToggle = (layer) => {
    setVisibleLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  // variable options (we can add new datasets/sources easily)
  const variableOptions = [
    { label: "SAWTI", value: "sawti", source: "COE"},
    { label: "Temperature", value: "mtTemp1", source: "CHGE" },
    { label: "Dewpoint", value: "mtDewPoint", source: "CHGE" },
    { label: "Wind Speed", value: "mtWindSpeed.av", source: "CHGE" },
    { label: "10Hr DFM", value: "tenhrfm", wims_name:"10HourDFM(%)", nfdrs_name:"10", source: "NFDRS" },
    { label: "100Hr DFM", value: "hunhrfm", wims_name:"100HourDFM(%)", nfdrs_name:"HU", source: "NFDRS" },
    { label: "1000Hr DFM", value: "thhrfm", wims_name:"1000HourDFM(%)", nfdrs_name:"TH", source: "NFDRS" },
    { label: "Ignition Component", value: "ignition_component", wims_name:"IC", nfdrs_name:"IC", source: "NFDRS" },
    { label: "Spread Component", value: "spread_component", wims_name:"SC", nfdrs_name:"SC", source: "NFDRS" },
    { label: "Energy Release Component", value: "energy_release_component", wims_name:"ERC", nfdrs_name:"ERC", source: "NFDRS" },
    { label: "Burning Index", value: "burning_index", wims_name:"BI", nfdrs_name:"BI", source: "NFDRS" },
    { label: "KBDI", value: "kbdi", wims_name:"KBDI", nfdrs_name:"KBDI", source: "NFDRS" },

  ];

  // Fetch data when a variable is selected
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedVariable) {
        setStationData([]);
        return;
      }

      try {
        //const ymd = selectedDate.replace(/-/g, "")
        //const datetime_ny = selectedDateTime.tz("America/New_York");
        // const datetime_ny = selectedDateTime;

        // Find the selected variable config (to get its source)
        const variableConfig = variableOptions.find(
          (v) => v.value === selectedVariable
        );

        if (!variableConfig) return;

        let urls = [];

        // Fetch based on source (you can expand this later)
        if (variableConfig.source === "CHGE") {
          urls = [
            `${domain}/api/index.php/get_data_by_hour/${selectedDateTime.format("YYYYMMDD")}/${selectedDateTime.format("HH")}`,
          ];
            // Fetch all URLs
            const responses = await Promise.all(urls.map((u) => fetch(u)));
            const dataArrays = await Promise.all(responses.map((r) => r.json()));

            // Merge arrays (each should have lat/lon)
            const merged = dataArrays.flat();
            setStationData(merged);
            setIsNFDRS(false);
        }

        else if (variableConfig.source === "NFDRS") {
            getNFDRS(selectedDateTime.format("YYYY-MM-DD")).then((json_data) => {
                if (Array.isArray(json_data) && json_data.length > 0) {
                    setStationData(json_data);
                    
                    const mTime = json_data[0]?.DateTime;
                    setNfdrsMtime(mTime || null);
                    
                    setIsNFDRS(true);
                }
            }).catch((error) => {
                setNfdrsMtime(null)
                setStationData(null);
                console.log(error)
            });
        }

        else if (variableConfig.source === "COE") {
            const archiveUrl = `${domain}/api/index.php/get_sawti_nysm/archive/${selectedDateTime.format("YYYYMMDD")}`;
            //const latestUrl  = `${domain}/api/index.php/get_sawti_nysm/latest`;

            // Helper function to load and parse an endpoint
            const fetchJson = async (url) => {
                try {
                    const res = await fetch(url);
                    if (!res.ok) return null;
                    return await res.json();
                } catch (err) {
                    return null;
                }
            };

            let result = await fetchJson(archiveUrl);

            if (
                !result ||
                !result.data ||
                (Array.isArray(result.data) && result.data.length === 0) ||
                (typeof result.data === "object" && Object.keys(result.data).length === 0)
            ) {
                console.warn("Archive empty");
                //result = await fetchJson(latestUrl);
            }


            const { last_modified, data } = result;

            setStationData(data);
            setLastModified(last_modified);
            setIsNFDRS(false);
        }


        


      } catch (err) {
        console.error("Error fetching station data:", err);
        setStationData([]);
      }
    };

    fetchData();
  }, [selectedVariable, selectedDateTime, display]);

  useEffect(() =>{

    const fetchData = async () => {
      if (!selectedVariable) {
        setTimeSeriesData([]);
        return;
      }
      try {
        // Find the selected variable config (to get its source)
        const variableConfig = variableOptions.find(
          (v) => v.value === selectedVariable
        );

        if (!variableConfig) return;

        let urls = [];

        // Fetch based on source (you can expand this later)
        if (variableConfig.source === "CHGE") {
            
          urls = [
            `${domain}/api/index.php/get_data_range/${chart_start_date.format("YYYYMMDD")}/${now.format("YYYYMMDD")}/${selectedStation}`,
          ];

            // Fetch all URLs
            const responses = await Promise.all(urls.map((u) => fetch(u)));
            const dataArrays = await Promise.all(responses.map((r) => r.json()));

            // Merge arrays (each should have lat/lon)
            const merged = dataArrays.flat();
            setTimeSeriesData(merged);
        }

        // Fetch based on source (you can expand this later)
        if (variableConfig.source === "COE") {
            
          urls = [
            `${domain}/api/index.php/get_sawti_nysm/timeseries/${selectedStation}/${chart_start_date.format("YYYYMMDD")}/${now.format("YYYYMMDD")}`,
          ];

            // Fetch all URLs
const responses = await Promise.all(
    urls.map((u) => fetch(u))
);

const jsonResponses = await Promise.all(
    responses.map((r) => r.json())
);

const merged = jsonResponses.flatMap(
    (r) => r.data ?? []
);

setTimeSeriesData(merged);

            //const responses = await Promise.all(urls.map((u) => fetch(u)));
            // const dataArrays = await Promise.all(responses.map((r) => r.json()));
            // // Merge arrays (each should have lat/lon)
            // const merged = dataArrays.flat();
            // setTimeSeriesData(merged);
        }

        if (variableConfig.source === "NFDRS") {

            // Get data fro NFDRS time series
            getNFDRSbyStn(chart_start_date.format("YYYY-MM-DD"), now.format("YYYY-MM-DD"), selectedStation).then((json_data) => {
                if (Array.isArray(json_data) && json_data.length > 0) {
                    const merged = json_data.flat();               
                    setTimeSeriesData(merged);
                    console.log(merged);
                }
            })
            
            var station_info = wims_meta.find(stn => stn["stid"] === selectedStation);
            const variableConfig = variableOptions.find(
                (v) => v.value === selectedVariable
            );
            if (!variableConfig) return;


            // Get data fro NFDRS time series
            getNfdrsClimo(station_info['name'], variableConfig['nfdrs_name']).then((json_data) => {
                if (Array.isArray(json_data) && json_data.length > 0) {
                    const normals = json_data.flat();
                    setTimeSeriesNormals(normals);
                    console.log(normals);
                }
            })

        } else {
            setTimeSeriesNormals([]);
        }




      } catch (err) {
        console.error("Error fetching station data:", err);
        setTimeSeriesData([]);
      }
    };
    fetchData();

  },[selectedStation, selectedVariable, display]);


  
    useEffect(() => {
        if (display !== "forecast" || !forecastSelection) return;

        const { model, day } = forecastSelection;

        const fetchForecast = async () => {
            const url = `${domain}/api/index.php/get_sawti/${day}/${model}/geojson`
            const res = await fetch(url);
            const json = await res.json();

            setForecastData(json);
            setIsNFDRS(false);
        };

        fetchForecast().catch(console.error);
    }, [display, forecastSelection]);


return (
  <div
    className="App"
    style={{
      display: "flex",
      height: "100vh",
      overflow: "hidden",
      gap: "12px",
    //   padding: "12px",
    }}
  >
    {/* Sidebar */}
    <div
      className="surface"
      style={{
        width: "280px",
        padding: "15px",
        flexShrink: 0,
      }}
    >
      <LayerControls
        visibleLayers={visibleLayers}
        onLayerToggle={handleLayerToggle}
        selectedVariable={selectedVariable}
        onVariableChange={setSelectedVariable}
        variableOptions={variableOptions}
        selectedDateTime={selectedDateTime}
        onDateTimeChange={setSelectedDateTime}
        display_mode={display}
        sawti_modified_dt={lastModified}
        nfdrs_modified_dt={nfdrs_modified_dt}
      />
    </div>

    {/* Main Content */}
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateRows: " auto auto 1fr auto",
        gap: "12px",
        minHeight: 0, // important for overflow
      }}
    >
      {/* Toggle Row */}
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          style={{width: "20%", margin: "5px"}}
          className={display === "obs" ? "button--primary" : "button--outline"}
          onClick={() => setDisplay("obs")}
        >
          Observations
        </button>
        <button
          style={{width: "20%", margin: "5px"}}
          className={display === "forecast" ? "button--primary" : "button--outline"}
          onClick={() => setDisplay("forecast")}
        >
          Forecast
        </button>
      </div>


    <div
        style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "12px",
        }}
    >


    {/* Info Row */}
    {display === "obs" && (
    <div
        style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "12px",
        alignItems: "stretch",
        }}
    >

        {/* DEC Risk */}
        <div
        className="surface--card"
        style={{
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minWidth: 0,
        }}
        >
        <div
            style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "12px",
            height: "100%",
            }}
        >

            <div>
            <h4 style={{ marginBottom: "6px" }}>DEC Risk</h4>

            {catskillRisk && (
                <>
                <p>Effective: {catskillRisk.effective_date}</p>
                <p>Valid through: {catskillRisk.valid_through}</p>
                </>
            )}
            </div>

            <div>
            <h4 style={{ marginBottom: "6px" }}>
                Hudson Valley
            </h4>

            <h1
                style={{
                color:
                    nfdrs_risk_colors[
                    risk_ids[hudsonRisk?.risk]
                    ],
                margin: 0,
                }}
            >
                {
                hudsonRisk?.risk &&
                risk_ids[hudsonRisk.risk] || "-"
                }
            </h1>
            </div>

            <div>
            <h4 style={{ marginBottom: "6px" }}>
                Catskill
            </h4>

            <h1
                style={{
                color:
                    nfdrs_risk_colors[
                    risk_ids[catskillRisk?.risk]
                    ],
                margin: 0,
                }}
            >
                {
                catskillRisk?.risk &&
                risk_ids[catskillRisk.risk] || "-"
                }
            </h1>
            </div>

        </div>
        </div>

        {/* SAWTI */}
        <div
            className="surface--card"
            style={{
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
            }}
            >
            <SawtiCalendar />
        </div>

    </div>
    )}

        {display === "forecast" && (
            <div>
                <div
                    style={{
                    display: "grid",
                    height: "150px",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "12px",
                    }}
                >
                <div className="surface--card" style={{ padding: "10px" }}>
                    <h3>
                        Forecasted Fire Danger:{" "}
                    </h3>

                    <h3>
                    <strong>
                    {forecastSelection.model} – Day {forecastSelection.day}
                    </strong>
                    </h3>

                </div>

                    <ForecastSelector
                        selection={forecastSelection}
                        onChange={setForecastSelection}
                    />

                </div>


            </div>

        )}

      
      </div>


      {/* Map */}
      <div className="surface" style={{ overflow: "hidden" }}>
        <MapContainer
          visibleLayers={visibleLayers}
          stationData={stationData}
          selectedVariable={selectedVariable}
          setStationName={setSelectedStation}
          isNFDRS={isNFDRS}
          display={display}
          forecastData={forecastData}
          colorInfo={colorInfo}
        //   forecastDisplayMode={forecastDisplayMode}
        />
      </div>



      {/* Time Series */}
      <div
        className="surface"
        style={{
          display: selectedStation ? "block" : "none",
          overflow: "hidden",
        }}
      >
        <TimeSeriesChart
          data={timeSeriesData}
          statsData={timeSeriesNormals}
          selectedVar={selectedVariable}
          variableOptions={variableOptions}
        />
      </div>

    { (display == "forecast") && (
        <div>
        <Colorbar 
            scale={colorMapping["sawti"]}

        />
        </div>
    )}
    { (display == "obs") && (
        <div>
        <Colorbar 
            scale={colorMapping[selectedVariable]}

        />
        </div>
    )}


    </div>
  </div>
);

};

export default App;
